import { useState } from "react";
import { Copy, Globe, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero } from "../components/PageShell";
import { Reveal } from "../components/Reveal";
import { EMAIL } from "../data/content";

// const helpOptions = [
//   "A new product or platform",
//   "Modernizing an existing system",
//   "Automating a business process",
//   "AI integration",
//   "Ongoing development support",
//   "Something else",
// ];

// const projectTypes = [
//   "Custom Software",
//   "Web / Enterprise Application",
//   "API & Backend Engineering",
//   "Business Automation",
//   "AI & Intelligent Solutions",
//   "Legacy Modernization",
//   "Cloud & DevOps",
//   "Support & Enhancement",
// ];

// const budgetRanges = ["Under $5k", "$5k – $15k", "$15k – $50k", "$50k+", "Not sure yet"];

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  // helpWith: helpOptions[0],
  // projectType: projectTypes[0],
  // budget: budgetRanges[4],
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Project Inquiry — ${form.name}${form.company ? ` · ${form.company}` : ""}`
    );
    const body = encodeURIComponent(
      [
        `Name: ${form.name}`,
        `Company: ${form.company}`,
        `Email: ${form.email}`,
        `Phone: ${form.phone}`,
        // `Help needed: ${form.helpWith}`,
        // `Project type: ${form.projectType}`,
        // `Budget range: ${form.budget}`,
        "",
        "Project details:",
        form.message,
      ].join("\n")
    );
    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
    toast.success("Your email app is opening — the inquiry is ready to send.");
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      toast.success("Email address copied to clipboard.");
    } catch {
      toast.info(`Email us at ${EMAIL}`);
    }
  };

  return (
    <PageShell>
      <div data-testid="contact-page">
        <PageHero
          overline="Contact"
          title="Let's Build Something That Matters."
          description="Have an idea, an existing system that needs improvement, or a business process that could be smarter? Tell us what you're working on. We'll start with the problem — then explore the right technology."
        />

        <section className="py-20 lg:py-28">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
            <Reveal className="lg:col-span-7">
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="card-gradient rounded-2xl border border-white/10 p-7 sm:p-10"
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">
                  Start a conversation
                </p>
                <div className="mt-7 grid gap-5 sm:grid-cols-2">
                  <input
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Name *"
                    data-testid="contact-name-input"
                    className="field"
                  />
                  <input
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Company"
                    data-testid="contact-company-input"
                    className="field"
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Email *"
                    data-testid="contact-email-input"
                    className="field"
                  />
                  <input
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone"
                    data-testid="contact-phone-input"
                    className="field"
                  />
                  {/* <select
                    value={form.helpWith}
                    onChange={set("helpWith")}
                    data-testid="contact-help-select"
                    className="field"
                  >
                    {helpOptions.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                  <select
                    value={form.projectType}
                    onChange={set("projectType")}
                    data-testid="contact-project-type-select"
                    className="field"
                  >
                    {projectTypes.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                  <select
                    value={form.budget}
                    onChange={set("budget")}
                    data-testid="contact-budget-select"
                    className="field sm:col-span-2"
                  >
                    {budgetRanges.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select> */}
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us about your project *"
                    data-testid="contact-message-input"
                    className="field resize-none sm:col-span-2"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  className="spark-gradient group mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-display text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  Send Inquiry
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <p className="mt-4 font-mono text-[11px] tracking-wide text-slate-600">
                  Opens your email app with everything pre-filled.
                </p>
              </form>
            </Reveal>

            <div className="space-y-4 lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8" data-testid="contact-email-card">
                  <span className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-obsidian">
                    <Mail className="h-5 w-5 text-cyan-400" strokeWidth={1.75} />
                  </span>
                  <h2 className="mt-5 font-display text-xl font-semibold text-white">Prefer Email?</h2>
                  <a
                    href={`mailto:${EMAIL}`}
                    data-testid="contact-direct-email-link"
                    className="mt-2 block break-all text-sm text-cyan-300 transition-colors duration-300 hover:text-white"
                  >
                    {EMAIL}
                  </a>
                  <button
                    onClick={copyEmail}
                    data-testid="contact-copy-email-btn"
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-display text-xs font-semibold text-slate-200 transition-colors duration-300 hover:border-cyan-400/50 hover:text-white"
                  >
                    <Copy className="h-3.5 w-3.5 text-cyan-400" />
                    Copy address
                  </button>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8" data-testid="contact-global-card">
                  <span className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-obsidian">
                    <Globe className="h-5 w-5 text-cyan-400" strokeWidth={1.75} />
                  </span>
                  <h2 className="mt-5 font-display text-xl font-semibold text-white">Global Delivery</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    Based in India. Working with clients globally.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="rounded-2xl border border-dashed border-white/10 p-8">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">
                    What happens next
                  </p>
                  <ul className="mt-5 space-y-4">
                    {[
                      "We read every inquiry personally.",
                      "You hear back with honest first thoughts.",
                      "We start with the problem — then the technology.",
                    ].map((line, i) => (
                      <li key={line} className="flex items-start gap-3 text-sm text-slate-400">
                        <span className="mt-0.5 font-mono text-[10px] tracking-[0.2em] text-cyan-400/80">
                          0{i + 1}
                        </span>
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </div>
    </PageShell>
  );
}
